import Keycloak from 'keycloak-js';
import {
  KEYCLOAK_AUTH_ERROR,
  KEYCLOAK_AUTH_SUCCESS,
  KEYCLOAK_IN_PROGRESS,
  KEYCLOAK_REFRESH_TRANSIENT_ERROR,
  SESSION_EXPIRED,
} from '../../actions/types';

import * as KeycloakSession from '~features/auth/KeycloakSession';
import { isImpersonationToken } from './impersonation';
// The leaf module, not the SearchPerformance barrel: this file is imported during
// app boot, and the barrel pulls in the feature's React components.
import {
  deriveGscCallbackOutcome,
  gscCallbackOutcomeToQuery,
  isGscOAuthPopupWindow,
  resolveGscOAuthRelayTarget,
} from '~features/SearchPerformance/gsc-connection/GscOAuthBridge';

const keycloakOrigin = () => window.location.origin.toString();

const localEnvConfig = {
  realm: 'prerender',
  url: 'http://localhost:8088/auth/',
  clientId: 'prerender-frontend',
  prerenderOrigin: 'http://localhost:8443',
};

const stagingEnvConfig = {
  realm: 'prerender',
  url: 'https://auth.prerender-staging.dev/auth/',
  clientId: 'prerender-frontend',
  prerenderOrigin: 'https://dashboard.prerender-staging.dev',
};

const stagingSamlEnvConfig = {
  realm: 'prerender-staging-saml',
  url: 'https://auth-staging-saml-vhf.prerender.io/auth/',
  clientId: 'prerender-frontend',
  prerenderOrigin: 'https://staging-saml-dashboard.prerender.io',
};

const prodEnvConfig = {
  realm: 'prerender',
  url: 'https://auth.prerender.io/auth/',
  clientId: 'prerender-frontend',
  prerenderOrigin: 'https://dashboard.prerender.io',
};

const prodNewEnvConfig = {
  realm: 'prerender',
  url: 'https://auth.prerender.io/auth/',
  clientId: 'prerender-frontend',
  prerenderOrigin: 'https://app.prerender.com',
};

const generateKeycloakConfigFromSSOEnvName = (environmentName) => ({
  realm: `prerender-${environmentName}`,
  url: `https://${environmentName}.auth.prerender.io/auth/`,
  clientId: 'prerender-frontend',
});

const predefinedCOnfigs = [prodNewEnvConfig, prodEnvConfig, localEnvConfig, stagingEnvConfig, stagingSamlEnvConfig];

const originRegex = /^https:\/\/([a-z0-9-_]+)\.app\.prerender\.io$/;

export const getSSOSiteName = () => {
  const origin = keycloakOrigin();
  const match = originRegex.exec(origin);
  return (match && match[1]) || false;
};

export const isSSOSite = () => !!getSSOSiteName();

// Resolves the config for this origin, or undefined when no entry matches.
// Separate from configForOrigin() because the fallback there hides the
// difference between "this origin is the main realm" and "this origin is
// unknown" — a distinction a security gate has to be able to make.
export const resolveConfigForOrigin = () => {
  const ssoSiteName = getSSOSiteName();
  if (ssoSiteName) return generateKeycloakConfigFromSSOEnvName(ssoSiteName);
  return predefinedCOnfigs.find((e) => e.prerenderOrigin === keycloakOrigin());
};

export const configForOrigin = () => {
  const config = resolveConfigForOrigin();

  if (!config) {
    console.error(`Couldn't find config for ${keycloakOrigin()}, falling back to the prod env config`);
    return prodEnvConfig;
  } else {
    return config;
  }
};

// Every realm other than this one federates to a customer identity provider:
// the per-customer SSO realms (`prerender-<site>`) and the SAML realms served
// off ordinary hostnames, like `prerender-staging-saml` on
// staging-saml-dashboard.prerender.io. Keyed on the realm rather than the
// hostname, because `isSSOSite()` recognizes only `<name>.app.prerender.io` and
// so reports that SAML host as not federated.
const MAIN_REALM = 'prerender';

export const isFederatedRealm = (realm) => realm !== MAIN_REALM;

// In a federated realm the browser flow redirects to the customer's identity
// provider (`identity-provider-redirector` is REQUIRED there, with a
// defaultProvider set), so the `forms` subflow never runs — and neither does
// the conditional OTP form hanging off it. A TOTP credential enrolled against
// such a realm can never be asked for, so nothing should offer to enroll one.
// Fails closed: an origin with no config entry is treated as federated, so the
// enrollment button is withheld rather than offered. configForOrigin() falls back
// to prod (realm `prerender`) for an unknown origin, which would otherwise read
// as "not federated" and offer a credential that may never gate a login — the
// wrong default for a compliance-adjacent control. A new SSO or SAML host added
// without a config entry therefore hides the button instead of lying about it.
export const federatesIdentity = () => {
  const config = resolveConfigForOrigin();
  return !config || isFederatedRealm(config.realm);
};

let keycloak = null;

export const getAccessToken = () => keycloak && keycloak.token;

export const isImpersonatedSession = () => isImpersonationToken(keycloak?.tokenParsed);

export const keycloakLogout = () => {
  if (keycloak) keycloak.logout({ redirectUri: `${window.location.origin.toString()}/` });
};

export const keycloakLogin = () => {
  if (!keycloak) return;
  // Use login(), not logout(): clearToken() has already removed idToken, and logout() always
  // sends id_token_hint which Keycloak rejects when the hint is missing.
  keycloak.login({ redirectUri: `${window.location.origin.toString()}/` });
};

// Application-Initiated Action: send the logged-in user through Keycloak's
// themed UPDATE_PASSWORD screen (keycloakify "Set your password") and back to
// the app, instead of the raw Keycloak account console which also exposes the
// editable consent fields (PRE-3239). redirectUri defaults to the current page.
export const keycloakUpdatePassword = (redirectUri = window.location.href) => {
  if (!keycloak) return;
  keycloak.login({ action: 'UPDATE_PASSWORD', redirectUri });
};

// The same Application-Initiated Action mechanism for authenticator (TOTP)
// enrollment. This is the only 2FA entry point the app offers: the Keycloak 26
// account console that used to host it is not themed and exposes the consent
// fields, and PRE-3239 deliberately routed away from it. The theme owns the
// screen this lands on (login-config-totp.ftl -> LoginConfigTotp), and it
// renders a Cancel button because the action is app-initiated.
//
// Enrollment only ever adds a credential — Keycloak accepts several TOTP
// credentials per user, and removing one still needs an admin.
export const keycloakConfigureTotp = (redirectUri = window.location.href) => {
  if (!keycloak) return;
  keycloak.login({ action: 'CONFIGURE_TOTP', redirectUri });
};

const OIDC_RESPONSE_PARAMS = ['code', 'state', 'session_state', 'iss', 'error', 'error_description', 'error_uri'];

// Google's OAuth redirect lands its single-use ?code&state on this route
// (see pages/GscOAuthCallbackPage.tsx) — they belong to Google, not Keycloak.
const GSC_OAUTH_CALLBACK_PATH = '/connections/gsc/callback';

// Keycloak 26 rejects redirect URIs containing OIDC response parameters, and
// keycloak-js uses location.href verbatim as the login redirect_uri. Our auth
// responses arrive in the fragment (response_mode=fragment), so query copies
// of these params are always stale — drop them before init.
const stripOidcResponseParamsFromLocation = () => {
  if (window.location.pathname === GSC_OAUTH_CALLBACK_PATH) return;
  const url = new URL(window.location.href);
  if (!OIDC_RESPONSE_PARAMS.some((param) => url.searchParams.has(param))) return;
  OIDC_RESPONSE_PARAMS.forEach((param) => url.searchParams.delete(param));
  window.history.replaceState(null, '', url.toString());
};

// The relay leg of the Google flow: the callback page forwards the query to
// visibility and leaves this origin immediately, so nothing on this leg needs a
// session. Initializing keycloak here only starts a race with that redirect
// (keycloak-js runs `onLoad: 'login-required'` before the router mounts). If
// keycloak wins, Google's single-use code is lost, and Keycloak 26 refuses the
// login anyway, because the strip above exempts this route and redirect_uri
// therefore still carries ?code&state.
//
// The relay target comes from the page's own source of truth, so guard and page
// cannot disagree about which legs leave this origin. The pathname check stays
// here, in the auth module: read without it, a stale ?code on an ordinary route
// would skip auth for the whole app.
//
// Scoped to the code leg on purpose. In the main tab the return leg
// (?gsc_selection_session_id / ?status=error) keeps normal auth, because a
// blocked popup runs that same page in the main tab and navigates on into the
// authenticated app. Inside the popup no leg needs a session, and initKeycloak
// stops earlier on `isGscOAuthPopupWindow`.
const isGscOAuthRelayLeg = () =>
  window.location.pathname === GSC_OAUTH_CALLBACK_PATH &&
  Boolean(resolveGscOAuthRelayTarget(window.location.search));

// A leg that does not relay still arrives with Google's params, and this route
// is exempt from the strip above so the relay leg can keep them byte-for-byte.
// Left in place they would 400 the login redirect under KC26, so swap them for
// the outcome the callback page derives from them — the page reads that outcome
// just as well, and keycloak then sees a clean redirect_uri.
const replaceGoogleParamsWithCallbackOutcome = () => {
  const url = new URL(window.location.href);
  if (!OIDC_RESPONSE_PARAMS.some((param) => url.searchParams.has(param))) return;
  url.search = gscCallbackOutcomeToQuery(deriveGscCallbackOutcome(url.searchParams)).toString();
  window.history.replaceState(null, '', url.toString());
};

export const initKeycloak = (dispatch) => {
  if (window.location.pathname === GSC_OAUTH_CALLBACK_PATH) {
    // The popup never needs a session: every leg it runs either relays onward or
    // postMessages the result to its opener and closes. A login redirect here
    // only strands the flow — it either eats Google's single-use code or dies on
    // the KC26 redirect_uri check (PRE-3214).
    if (isGscOAuthPopupWindow() || isGscOAuthRelayLeg()) return;
    replaceGoogleParamsWithCallbackOutcome();
  }
  stripOidcResponseParamsFromLocation();
  dispatch({ type: KEYCLOAK_IN_PROGRESS });
  const keycloakInitOptions = {
    onLoad: 'login-required',
    checkLoginIframe: false,
  };

  const keycloakOptions = configForOrigin();

  keycloak = new Keycloak(keycloakOptions);

  // We're extending the createLoginUrl logic, So keeping the orignaizal implementation referenced at _createLoginUrl
  keycloak._createLoginUrl = keycloak.createLoginUrl;
  keycloak.createLoginUrl = (opts) => {
    const url = keycloak._createLoginUrl(opts);
    const username = sessionStorage.getItem('username');

    if (username) {
      return url + `&username=${username}`;
    }

    return url;
  };

  keycloak.onAuthSuccess = () => {
    // Keycloak 26 stopped putting `session_state` in the tokens it issues — the OIDC
    // session id is now `sid`. The fallback covers a realm still served by an older
    // Keycloak; verified against staging 26.7, where `session_state` is absent.
    const sessionState = keycloak.tokenParsed?.sid ?? keycloak.tokenParsed?.session_state;
    const lastSessionState = KeycloakSession.getState();

    const isNewLogin = sessionState && sessionState !== lastSessionState;

    if (isNewLogin) {
      KeycloakSession.setState(sessionState);
      KeycloakSession.setFreshness('fresh');
    }

    dispatch({ type: KEYCLOAK_AUTH_SUCCESS });
  };

  keycloak.onAuthError = (authErr) => {
    dispatch({ type: KEYCLOAK_AUTH_ERROR, payload: authErr || 'unknown auth error' });
  };

  keycloak.onTokenExpired = () => {
    dispatch({ type: KEYCLOAK_IN_PROGRESS });

    keycloak
      .updateToken(180)
      .then(() => {
        dispatch({ type: KEYCLOAK_AUTH_SUCCESS });
      })
      .catch(() => {
        // keycloak-js clears tokens only on HTTP 400 from the refresh endpoint. Other failures
        // (network, 5xx) leave the existing token in place — treat those as transient. Don't
        // dispatch KEYCLOAK_AUTH_SUCCESS here: it would reset sessionExpired and close a modal
        // that a 401 elsewhere may have legitimately opened.
        const sessionIsGone = !keycloak.token || !keycloak.refreshToken;
        if (sessionIsGone) {
          dispatch({ type: SESSION_EXPIRED });
        } else {
          dispatch({ type: KEYCLOAK_REFRESH_TRANSIENT_ERROR });
        }
      });
  };

  keycloak.init(keycloakInitOptions).then(() => {
    // Disable the auto-redirect that clearToken() triggers on token refresh failure (400).
    // Without this, kc.login() fires before our SESSION_EXPIRED dispatch can render the modal.
    // The modal's "Sign in" button handles re-auth explicitly via keycloakLogin().
    // NOTE: loginRequired is an undocumented keycloak-js internal (verified on v18: clearToken()
    // calls kc.login() when set) — re-verify on any keycloak-js upgrade.
    keycloak.loginRequired = false;
  }).catch((e) => {
    console.log('Failed to initialize keycloak', e);
  });
};
